import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export type PublicPageProfile = {
  id: string;
  display_name: string | null;
  handle: string | null;
  slug: string | null;
  avatar_url: string | null;
  bio: string | null;
  headline: string | null;
  website_url: string | null;
  country_code: string | null;
  niches: string[] | null;
  languages: string[] | null;
  socials: Record<string, string> | null;
  is_public: boolean;
  open_to_work: boolean;
  pro_enabled: boolean;
  pro_type: string | null;
  pro_name: string | null;
  pro_logo_url: string | null;
  pro_website: string | null;
  pro_bio: string | null;
  account_mode: string;
  referral_code: string;
  created_at: string;
  is_ambassador: boolean;
  ambassador_title: string | null;
  og_image_url: string | null;
  seo_title: string | null;
  seo_description: string | null;
};

export type ProfileSection = {
  id: string;
  kind: string;
  title: string;
  subtitle: string | null;
  body: string | null;
  image_url: string | null;
  link_url: string | null;
  year: number | null;
  sort_order: number;
};

export type PublicProfileListing = {
  id: string;
  name: string;
  slug: string;
  headline: string | null;
  logo_url: string | null;
  cover_url: string | null;
  website_url: string | null;
  click_count: number | null;
};

export type ProfileArticle = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body: string;
  cover_url: string | null;
  link_url: string | null;
  published_at: string;
};

const PROFILE_COLUMNS =
  "id,display_name,handle,slug,avatar_url,bio,headline,website_url,country_code,niches,languages,socials,is_public,open_to_work,pro_enabled,pro_type,pro_name,pro_logo_url,pro_website,pro_bio,account_mode,referral_code,created_at,is_ambassador,ambassador_title,og_image_url,seo_title,seo_description";

function publicClient() {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  const url = process.env["SUPABASE_URL"]!;
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input: RequestInfo | URL, init?: RequestInit) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) {
          h.delete("Authorization");
        }
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

/** Public, SSR-safe read of a creator / professional page by slug or handle. */
export const getPublicProfilePage = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => {
    const slug = String(data?.slug ?? "")
      .trim()
      .toLowerCase()
      .slice(0, 60);
    if (!slug) throw new Error("Missing profile slug.");
    return { slug };
  })
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(data.slug);
    const filter = isUuid
      ? `slug.eq.${data.slug},handle.eq.${data.slug},id.eq.${data.slug}`
      : `slug.eq.${data.slug},handle.eq.${data.slug}`;
    const { data: rows } = await supabase
      .from("profiles")
      .select(PROFILE_COLUMNS)
      .or(filter)
      .limit(1);

    const profile = ((rows ?? [])[0] ?? null) as PublicPageProfile | null;
    if (!profile || profile.is_public !== true) {
      return {
        profile: null as PublicPageProfile | null,
        socials: [],
        services: [],
        posts: [],
        offers: [],
        sections: [] as ProfileSection[],
        articles: [] as ProfileArticle[],
        listings: [] as PublicProfileListing[],
      };
    }

    const userId = profile.id;
    const [socials, services, posts, offers, sections, articles, listings] = await Promise.all([
      supabase
        .from("social_accounts")
        .select("id,platform,url,handle,followers,posts,engagement_rate,avatar_url,title,verified,source,last_synced_at")
        .eq("user_id", userId)
        .eq("is_public", true)
        .order("followers", { ascending: false, nullsFirst: false }),
      supabase
        .from("creator_services")
        .select("id,title,kind,description,price_cents,delivery_days,is_active")
        .eq("user_id", userId)
        .eq("is_active", true)
        .order("price_cents", { ascending: true }),
      supabase
        .from("showcase_posts")
        .select("id,url,platform,title,caption,thumbnail_url,views,likes,sort_order")
        .eq("user_id", userId)
        .order("sort_order", { ascending: true })
        .limit(24),
      supabase
        .from("pro_offers")
        .select("id,title,offer_type,description,deliverables,budget_cents,slots,country_code,status,created_at")
        .eq("user_id", userId)
        .eq("status", "open")
        .order("created_at", { ascending: false })
        .limit(12),
      supabase
        .from("profile_sections")
        .select("id,kind,title,subtitle,body,image_url,link_url,year,sort_order")
        .eq("user_id", userId)
        .eq("is_public", true)
        .order("sort_order", { ascending: true })
        .limit(200),
      supabase
        .from("profile_articles")
        .select("id,slug,title,excerpt,body,cover_url,link_url,published_at")
        .eq("user_id", userId)
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(50),
      supabase
        .from("listings")
        .select("id,name,slug,headline,logo_url,cover_url,website_url,click_count")
        .eq("owner_user_id", userId)
        .eq("is_public", true)
        .eq("moderation_status", "approved")
        .order("created_at", { ascending: false })
        .limit(24),
    ]);

    return {
      profile: profile as PublicPageProfile | null,
      socials: socials.data ?? [],
      services: services.data ?? [],
      posts: posts.data ?? [],
      offers: offers.data ?? [],
      sections: (sections.data ?? []) as ProfileSection[],
      articles: (articles.data ?? []) as ProfileArticle[],
      listings: (listings.data ?? []) as PublicProfileListing[],
    };
  });
